import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import {
  FiArrowLeft,
  FiUser,
  FiPhone,
  FiMapPin,
  FiPackage,
  FiRotateCcw,
  FiCheckCircle,
} from "react-icons/fi";
import { useTheme } from "../../../contexts/ThemeContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import AppContext from "../../../contexts/AppContext";
import "../../../assets/css/seller/subscreens/returnView/ReturnView.css";

const ReturnView = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const {
    baseUrl,
    baseUrlForFiles,
    sellerAuthToken,
    sellerSelectedReturn: returnData,
    setSellerSelectedOrder, 
  } = useContext(AppContext);

  const [status, setStatus] = useState(returnData?.status || "");
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (!returnData) navigate("/seller/returns", { replace: true });
  }, [returnData?.id]);

  if (!returnData) return null;

  const order = returnData.order || {};
  const isLocked = status !== "Pending";

  const orderCart = order.cart || [];
  const totalItems = orderCart.reduce((sum, i) => sum + i.quantity, 0);
  const orderTotal =
    order.total_amount != null
      ? Number(order.total_amount)
      : orderCart.reduce((sum, i) => sum + Number(i.price) * i.quantity, 0);

  const updateStatus = (newStatus) => {
    const formdata = new FormData();
    formdata.append("return_id", returnData.id);
    formdata.append("status", newStatus);
    axios
      .post(`${baseUrl}/seller/return/updateStatus`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then(() => {
        setStatus(newStatus);
        toast.success("Status updated!");
      })
      .catch(() => toast.error("Failed to update. Try again."));
  };

  const getStatusClass = (s) => {
    const map = {
      Pending: "rv-status-pending",
      Cancelled: "rv-status-cancelled",
      Returned: "rv-status-returned",
    };
    return map[s] || "";
  };

  const handleGoToOrder = () => {
    if (typeof setSellerSelectedOrder === "function") {
      setSellerSelectedOrder(order);
      navigate("/seller/orders/order-view"); // ⚠️ confirm this route matches your seller order view
    }
  };

  return (
    <SellerMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className={`rv-shell ${isDark ? "dark-mode" : ""}`}>
        <div className="rv-content-wrapper">
          <button
            className="rv-back-btn"
            onClick={() => navigate("/seller/returns")}
          >
            <FiArrowLeft /> Back to Returns
          </button>

          <div className="rv-header">
            <div className="rv-header-left">
              <h1 className="rv-header-id">Return #{returnData.id}</h1>
              <div className="rv-header-meta">
                <span className="rv-header-date">
                  Order #{returnData.chorder_id} •{" "}
                  {new Date(returnData.created_at).toLocaleDateString(
                    "en-PK",
                    { day: "numeric", month: "short", year: "numeric" },
                  )}
                </span>
                <span className={`rv-header-status ${getStatusClass(status)}`}>
                  {status}
                </span>
              </div>
            </div>
            <div className="rv-header-right">
              <div className="rv-header-total">
                <span className="rv-total-label">Order Total</span>
                <span className="rv-total-amount">
                  Rs {orderTotal.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="rv-layout">
            <div className="rv-left">
              <div className="rv-card">
                <h3 className="rv-card-title">
                  <FiUser /> Customer Details
                </h3>
                <div className="rv-info-list">
                  <div className="rv-info-row">
                    <div className="rv-info-icon">
                      <FiUser />
                    </div>
                    <div className="rv-info-data">
                      <span className="rv-info-label">Name</span>
                      <span className="rv-info-value">{order.full_name}</span>
                    </div>
                  </div>
                  <div className="rv-info-row">
                    <div className="rv-info-icon">
                      <FiPhone />
                    </div>
                    <div className="rv-info-data">
                      <span className="rv-info-label">Phone</span>
                      <span className="rv-info-value">{order.phone}</span>
                    </div>
                  </div>
                  <div className="rv-info-row">
                    <div className="rv-info-icon">
                      <FiMapPin />
                    </div>
                    <div className="rv-info-data">
                      <span className="rv-info-label">Address</span>
                      <span className="rv-info-value">{order.address}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rv-card">
                <h3 className="rv-card-title">
                  <FiPackage /> Order Items
                </h3>
                <div className="rv-products-list">
                  {orderCart.map((item) => {
                    const imgSrc =
                      item.product?.images?.length > 0
                        ? `${baseUrlForFiles}/${item.product.images[0].image_path}`
                        : "/placeholder.png";
                    const hasSize =
                      item.size && item.size !== "null" && item.size !== "";
                    const colorsStr = item.product?.variant?.colors;
                    const hasColor =
                      colorsStr && colorsStr.trim() !== "";
                    const colorValue = hasColor
                      ? colorsStr.split(",")[item.color] ?? "transparent"
                      : null;
                    return (
                      <div
                        key={item.id}
                        className="rv-product-item rv-product-item-clickable"
                        onClick={handleGoToOrder}
                        title="View full order"
                      >
                        <div className="rv-product-main">
                          <img
                            src={imgSrc}
                            alt={item.product?.product_name}
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: 8,
                              objectFit: "cover",
                              flexShrink: 0,
                            }}
                          />
                          <div className="rv-product-info">
                            <h4 className="rv-product-name">
                              {item.product?.product_name}
                            </h4>
                            <div className="rv-product-meta">
                              <span>Qty: {item.quantity}</span>
                              <span>•</span>
                              <span className="rv-product-price">
                                Rs {Number(item.price).toLocaleString()}
                              </span>
                              {hasSize && (
                                <>
                                  <span>•</span>
                                  <span>Size: {item.size}</span>
                                </>
                              )}
                              {hasColor && (
                                <>
                                  <span>•</span>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    Color:
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        marginLeft: 4,
                                        verticalAlign: "middle",
                                        border: "1px solid #ccc",
                                        backgroundColor: colorValue,
                                      }}
                                    />
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="rv-products-total">
                  <span className="rv-products-total-label">Total Items:</span>
                  <span className="rv-products-total-count">{totalItems}</span>
                </div>
              </div>
            </div>

            <div className="rv-right">
              <div className="rv-card">
                <h3 className="rv-card-title">
                  <FiRotateCcw /> Return Details
                </h3>
                <div className="rv-info-list">
                  <div className="rv-info-data">
                    <span className="rv-info-label">Reason</span>
                    <span className="rv-info-value">{returnData.reason}</span>
                  </div>
                  <div className="rv-info-data">
                    <span className="rv-info-label">Description</span>
                    <span className="rv-info-value">
                      {returnData.description}
                    </span>
                  </div>
                </div>
                {returnData.images?.length > 0 && (
                  <div className="rv-proof-images">
                    {returnData.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={`${baseUrlForFiles}/${img.image_path}`}
                        alt={`Return proof ${idx + 1}`}
                        className="rv-proof-image"
                        onClick={() =>
                          setLightboxImage(
                            `${baseUrlForFiles}/${img.image_path}`,
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="rv-card">
                <h3 className="rv-card-title">
                  <FiCheckCircle /> Return Status Management
                </h3>
                <div className="rv-status-content">
                  <div className="rv-status-dropdown-wrapper">
                    <label className="rv-status-label">Current Status</label>
                    <select
                      className="rv-status-dropdown"
                      value={status}
                      onChange={(e) => updateStatus(e.target.value)}
                      disabled={isLocked}
                    >
                      <option value="Pending" disabled>
                        Pending
                      </option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Returned">Returned</option>
                    </select>
                  </div>

                  {isLocked && (
                    <div className="rv-status-locked">
                      <FiCheckCircle />
                      <span>Status is locked and cannot be changed</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxImage && (
        <div
          className="rv-lightbox-overlay"
          onClick={() => setLightboxImage(null)}
        >
          <img
            src={lightboxImage}
            alt="Preview"
            className="rv-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </SellerMasterLayout>
  );
};

export default ReturnView;